import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoomAssignmentModal = ({ 
  isOpen, 
  onClose, 
  batch, 
  onAssignmentComplete 
}) => {
  const [rooms, setRooms] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingRooms, setFetchingRooms] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableRooms();
    }
  }, [isOpen]);

  const fetchAvailableRooms = async () => {
    setFetchingRooms(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get all rooms and active room cycles
      const [roomsRes, cyclesRes] = await Promise.all([
        axios.get(`${API}/rooms`, { headers }),
        axios.get(`${API}/room-cycles?status=active`, { headers })
      ]);
      
      const allRooms = roomsRes.data;
      // /room-cycles returns { cycles: [...] } — unwrap to an array
      const activeCycles = Array.isArray(cyclesRes.data)
        ? cyclesRes.data
        : (cyclesRes.data?.cycles || []);
      
      // Find rooms that don't have active cycles (idle rooms)
      const occupiedRoomIds = new Set(activeCycles.map(c => c.room_id));
      const idle = allRooms.filter(r => !occupiedRoomIds.has(r.id));
      
      setRooms(allRooms);
      setAvailableRooms(idle);
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
      toast.error('Failed to load available rooms');
    } finally {
      setFetchingRooms(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedRoomId) {
      toast.error('Please select a room');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Assign (or change) the room for this batch. Works even if the batch was
      // already marked completed without a room. Frees the old room when changing.
      const response = await axios.post(`${API}/compost/batches/${batch.id}/assign-room`, {
        room_id: selectedRoomId,
        notes: notes || `Spawning from ${batch.batch_name}`
      }, { headers });

      const selectedRoom = rooms.find(r => r.id === selectedRoomId);
      toast.success(`${batch.batch_name} assigned to ${selectedRoom?.name || 'room'}! Room cycle started.`);
      
      if (onAssignmentComplete) {
        onAssignmentComplete(response.data);
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to assign batch to room:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign batch to room');
    } finally {
      setLoading(false);
    }
  };

  if (!batch) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="room-assignment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#1A4D2E]" />
            Assign Batch to Growing Room
          </DialogTitle>
          <DialogDescription>Select an idle room to start this batch's growing cycle. Changing rooms frees the previous one.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Batch Info */}
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-green-900">{batch.batch_name}</p>
                <p className="text-sm text-green-700">Composting Complete - Ready for Spawning</p>
              </div>
              <Badge className="bg-green-600 text-white">Day 20</Badge>
            </div>
          </div>

          {/* Room Selection */}
          <div className="space-y-2">
            <Label htmlFor="room-select">Select Growing Room</Label>
            {fetchingRooms ? (
              <div className="text-sm text-gray-500">Loading available rooms...</div>
            ) : availableRooms.length === 0 ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">No idle rooms available</span>
                </div>
                <p className="text-sm text-yellow-700 mt-1">
                  All rooms currently have active cycles. Wait for a room to complete its cycle.
                </p>
              </div>
            ) : (
              <Select value={selectedRoomId} onValueChange={setSelectedRoomId}>
                <SelectTrigger data-testid="room-select">
                  <SelectValue placeholder="Choose a room..." />
                </SelectTrigger>
                <SelectContent>
                  {availableRooms.map((room) => (
                    <SelectItem key={room.id} value={room.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{room.name}</span>
                        <Badge variant="outline" className="bg-gray-100 text-gray-600 text-xs">
                          Idle
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this assignment..."
              className="resize-none"
              rows={3}
              data-testid="assignment-notes"
            />
          </div>

          {/* Info about what happens next */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-900 mb-2">What happens next:</p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-blue-600" />
                45-day room cycle will start automatically
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-blue-600" />
                All key dates (Casing, Ruffling, Flush 1, etc.) calculated
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-blue-600" />
                Calendar updated for Owner, Grower & Supervisors
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssign} 
            disabled={loading || !selectedRoomId || availableRooms.length === 0}
            className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
            data-testid="confirm-assignment-btn"
          >
            {loading ? (
              'Assigning...'
            ) : (
              <>
                <ArrowRight className="w-4 h-4 mr-2" />
                Assign & Start Cycle
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RoomAssignmentModal;
